import React from "react";
import { useQuery } from "@tanstack/react-query";
import ClaimFlow from "@/components/ClaimFlow";
import Footer from "@/components/Footer";
import Link from "@/components/Link";
import StoreWidgetPreview, {
  ShopifySection,
  isWidgetEligible,
} from "@/components/StoreWidgetPreview";
import { useSearchParams } from "@/hooks/useSearchParams";
import { useAuth } from "@/contexts/AuthContext";
import { fetchStore } from "@/api/storeService";
import { reviewService } from "@/api/reviewService";
import type { Review } from "@/types/review";

// /claim — the public claim funnel + status home (D-06, outside the gated /merchant area).
// Reads the ?store=<slug> param carried in from the repurposed ClaimBanner CTA (and from the
// Outreach admin claim-link generator) to pre-select the store and skip to the evidence step.
//
// When ?store= resolves to a real store, the page also renders the widget preview that used to
// live on the standalone outreach demo route — for EVERY visitor, signed in or not. Signed-out
// visitors additionally get a short "what is Yetfarrag" overview between the preview and the
// (unchanged) sign-in gate card, because they have no other context before the gate.
//
// This file is page framing ONLY — the funnel itself lives in ClaimFlow (search → evidence →
// status) and is intentionally left untouched. The header + "what happens next" + support
// blocks below give the page context so the funnel card doesn't float on empty background.
// Brand skin: RTL, Cairo, --ys-* tokens, weights 400 + 600 only (matches /for-business).

const font = "var(--ys-font)";
const SUPPORT_WHATSAPP = "https://wa.me/201555987590";
const COLUMN = 560;

type Step = { title: string; body: string };

const WHAT_HAPPENS: Step[] = [
  {
    title: "ابعت طلبك",
    body: "اختَر متجرك (أو أضِف رابطه) وارفع إثبات بسيط إنك صاحبه.",
  },
  {
    title: "بنراجعه",
    body: "فريقنا يراجع الطلب ويعلمك بالنتيجة، عادةً خلال ٤٨ ساعة.",
  },
  {
    title: "لوحة متجرك",
    body: "بعد الموافقة تفتح لك لوحة التحكم للرد على التقييمات ومتابعة سمعتك.",
  },
];

export default function Page() {
  const [searchParams] = useSearchParams();
  const store = searchParams.get("store") || undefined;
  const { user, userCheckingFinished } = useAuth();

  // fetchStore THROWS on a non-200 (Arabic message) — retry:false + the isError branch is what
  // makes an unknown/typo'd ?store= degrade silently (no preview) instead of erroring the page.
  // `enabled` keeps the no-param /claim render byte-identical to what it was before: no query
  // fires, no preview, no overview.
  const { data, isLoading, isError } = useQuery({
    queryKey: ["claim", "store", store],
    queryFn: () => fetchStore({ id: store as string }),
    enabled: !!store,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const resolvedStore = !isError && data?.data ? data.data : null;
  const eligible = isWidgetEligible(resolvedStore);

  // The store's real reviews, for the widget preview. Gated on `eligible` so a thin or unknown
  // store fires no extra request at all (it would only ever render the synthetic sample anyway).
  // limit 6 = the 3 rows x 2 columns the preview grid shows, top-rated first (this is a sales
  // surface — lead with the store's best reviews).
  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ["claim", "reviews", resolvedStore?._id],
    queryFn: () =>
      // The cast is required, not sloppiness: request() already unwrapped { ok, data }, so the
      // runtime value is the inner object while the declared type still carries the wrapper.
      // Same workaround as the store page's ReviewsSection.tsx.
      reviewService.getStoreReviews({
        storeId: resolvedStore!._id,
        limit: 6,
        sortBy: "rating_high",
      }) as unknown as Promise<{ reviews: Review[] }>,
    enabled: eligible && !!resolvedStore?._id,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // On error or an empty page this stays null and StoreWidgetPreview falls back to the labelled
  // sample. No error UI: a prospect does not need to hear about our fetch failing.
  const previewReviews = reviewsData?.reviews ?? null;

  // Render nothing while in flight rather than flashing the wrong preview mode. An ELIGIBLE store
  // also waits for the reviews query, otherwise the merchant sees the synthetic sample flip to
  // their real reviews a moment later. `reviewsLoading` goes false on error too, so a failed
  // reviews fetch still shows the preview (as the labelled sample) rather than hiding it.
  const showPreview =
    !!store && !isLoading && !!resolvedStore && (!eligible || !reviewsLoading);
  // Gate on userCheckingFinished so the overview never flashes for a signed-in visitor mid-check.
  const showOverview = showPreview && userCheckingFinished && !user;

  const bodyText: React.CSSProperties = {
    fontFamily: font,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 1.6,
    color: "var(--ys-ink-2)",
    margin: 0,
  };

  return (
    <>
    <div
      dir="rtl"
      style={{
        background: "var(--ys-bg)",
        minHeight: "100vh",
        padding: "40px 16px 64px",
        fontFamily: font,
        color: "var(--ys-ink)",
      }}
    >
      {/* Back link */}
      <div style={{ maxWidth: COLUMN, margin: "0 auto 20px" }}>
        <Link
          to="/"
          style={{
            display: "inline-block",
            fontSize: 14,
            color: "var(--ys-ink-2)",
            textDecoration: "none",
          }}
        >
          → العودة إلى الصفحة الرئيسية
        </Link>
      </div>

      {/* Header */}
      <header
        style={{
          maxWidth: COLUMN,
          margin: "0 auto 24px",
          textAlign: "center",
        }}
      >
        <h1
          style={{
            fontFamily: font,
            fontSize: 28,
            fontWeight: 600,
            lineHeight: 1.2,
            color: "var(--ys-ink)",
            margin: 0,
          }}
        >
          طالب بمتجرك على يتفرّج
        </h1>
        <p style={{ ...bodyText, fontSize: 16, margin: "12px auto 0", maxWidth: 480 }}>
          أكّد ملكيتك في خطوات بسيطة عشان ترد على تقيييمات متجرك، تحدّث بياناتك، وتتابع
          سمعتك مع عملائك في مكان واحد.
        </p>
      </header>

      {/* Widget preview — any visitor, signed in or not, whenever ?store= resolves */}
      {showPreview ? (
        <section style={{ maxWidth: 960, margin: "0 auto 32px" }}>
          <StoreWidgetPreview store={resolvedStore} reviews={previewReviews} />
        </section>
      ) : null}

      {/* Signed-out overview — context before the sign-in gate. Never shown when signed in. */}
      {showOverview ? (
        <section style={{ maxWidth: 960, margin: "0 auto 32px" }}>
          <div
            style={{
              background: "var(--ys-paper)",
              border: "1px solid var(--ys-rule)",
              borderRadius: "var(--ys-r-md)",
              boxShadow: "var(--ys-shadow-2)",
              padding: 24,
              marginBottom: 20,
            }}
          >
            <h2
              style={{
                fontFamily: font,
                fontSize: 20,
                fontWeight: 600,
                color: "var(--ys-ink)",
                margin: 0,
              }}
            >
              إيه هو يتفرّج؟
            </h2>
            <p style={{ ...bodyText, marginTop: 8 }}>
              يتفرّج منصّة تقييمات للمتاجر المصرية على الإنترنت. العملاء بيدوّروا على المتاجر،
              يقروا تجارب اللي اشتروا قبلهم، ويسيبوا تقييماتهم. متجرك موجود على المنصّة بالفعل،
              والمطالبة بيه بتديك التحكّم في بياناته والرد على تقييماته.
            </p>

            <h3
              style={{
                fontFamily: font,
                fontSize: 16,
                fontWeight: 600,
                color: "var(--ys-ink)",
                margin: "20px 0 0",
              }}
            >
              ليه التقييمات مهمة لمتجرك؟
            </h3>
            <p style={{ ...bodyText, marginTop: 8 }}>
              التقييمات الحقيقية بتبني ثقة العميل قبل ما يشتري، وبتخلّي متجرك يظهر لناس بتدوّر على
              اللي بتبيعه. لما تطالب بمتجرك تقدر ترد على كل تقييم، تحدّث بياناتك، وتركّب أداة
              التقييمات على موقعك عشان تعرض آراء عملائك عندك مباشرةً.
            </p>
          </div>

          <ShopifySection compact />
        </section>
      ) : null}

      {/* Funnel (unchanged) */}
      <ClaimFlow initialStore={store} />

      {/* What happens next */}
      <section style={{ maxWidth: COLUMN, margin: "32px auto 0" }}>
        <h2
          style={{
            fontFamily: font,
            fontSize: 16,
            fontWeight: 600,
            color: "var(--ys-ink-2)",
            margin: "0 0 12px",
          }}
        >
          إيه اللي بيحصل بعد ما تبعت طلبك؟
        </h2>
        <ol
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 12,
          }}
        >
          {WHAT_HAPPENS.map((step, i) => (
            <li
              key={step.title}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "flex-start",
                background: "var(--ys-paper)",
                border: "1px solid var(--ys-rule)",
                borderRadius: "var(--ys-r-md)",
                boxShadow: "var(--ys-shadow-2)",
                padding: 16,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  width: 28,
                  height: 28,
                  borderRadius: "var(--ys-r-pill)",
                  background: "var(--ys-primary-soft)",
                  color: "var(--ys-primary)",
                  fontFamily: font,
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {i + 1}
              </span>
              <div>
                <p
                  style={{
                    fontFamily: font,
                    fontSize: 16,
                    fontWeight: 600,
                    color: "var(--ys-ink)",
                    margin: 0,
                  }}
                >
                  {step.title}
                </p>
                <p style={{ ...bodyText, marginTop: 4 }}>{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        {/* Trust line */}
        <p
          style={{
            ...bodyText,
            fontSize: 13,
            color: "var(--ys-ink-3)",
            textAlign: "center",
            marginTop: 16,
          }}
        >
          مجاناً تماماً، من غير أي التزام.
        </p>

        {/* Support + learn more */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px 20px",
            marginTop: 20,
            paddingTop: 20,
            borderTop: "1px solid var(--ys-rule)",
            fontSize: 14,
          }}
        >
          <a
            href={SUPPORT_WHATSAPP}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--ys-primary)", fontWeight: 600, textDecoration: "none" }}
          >
            محتاج مساعدة؟ كلّمنا على واتساب
          </a>
          <Link
            to="/for-business"
            style={{ color: "var(--ys-ink-2)", fontWeight: 600, textDecoration: "none" }}
          >
            اعرف أكتر عن مميزات أصحاب المتاجر
          </Link>
        </div>
      </section>
    </div>
    <Footer variant="minimal" />
    </>
  );
}
